import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { useAccount } from "../../context/AccountContext";
import PayoffChart from "../../components/strategy/PayoffChart";
import { strategyLabel, STRATEGY_OPTIONS } from "../../constants/strategies";
import { formatCurrency, formatCurrencySigned, DEFAULT_CURRENCY_SYMBOL } from "../../utils/currency";

const StrategyManage = () => {
  const { selectedAccount, currencySymbol } = useAccount();
  const curSym = currencySymbol || DEFAULT_CURRENCY_SYMBOL;

  const [strategies, setStrategies] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  // Live option positions per DHSS symbol — populated when card expands
  const [dhssPositions, setDhssPositions] = useState({});  // { symbol: [rows] }

  // Pre-filter from URL ?symbol=XXX (set by Manage Strategy button on trade detail)
  const urlSymbol = new URLSearchParams(window.location.search).get("symbol") || "";
  const [togglingId, setTogglingId] = useState(null);
  const [openKebabId, setOpenKebabId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [msg, setMsg] = useState(null);
  const kebabRef = useRef(null);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 3000);
  };

  const [searchSymbol, setSearchSymbol] = useState(urlSymbol);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    if (selectedAccount) fetchStrategies();
  }, [selectedAccount]);

  useEffect(() => {
    const handler = (e) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target)) {
        setOpenKebabId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleRemove = async (strategy, e) => {
    e.stopPropagation();
    setOpenKebabId(null);
    if (!window.confirm(`Remove ${strategy.symbolname} (${strategyLabel(strategy.strategy_type)})? This cannot be undone.`)) return;
    setRemovingId(strategy.id);
    try {
      await axios.delete(`/strategy/${strategy.id}`);
      flash(`${strategy.symbolname} removed`);
      fetchStrategies();
    } catch (err) {
      flash(err.response?.data?.error || "Failed to remove strategy.", false);
    } finally {
      setRemovingId(null);
    }
  };

  const fetchStrategies = async () => {
    const res = await axios.get("/strategy/view", {
      params: { username: selectedAccount }
    });
    setStrategies(res.data);
  };

  const toggleCard = (id, strategy) => {
    const newId = expandedId === id ? null : id;
    setExpandedId(newId);
    // Fetch live positions when a DHSS card is expanded
    if (newId && strategy && strategy.strategy_type === "DELTA_HEDGED_SHORT_STRANGLE") {
      const sym = strategy.symbolname;
      const uname = selectedAccount;
      if (!dhssPositions[sym]) {
        axios.get("/trading/option-positions", { params: { username: uname } })
          .then(res => {
            const bySymbol = {};
            (res.data || []).forEach(p => {
              if (!bySymbol[p.symbolname]) bySymbol[p.symbolname] = [];
              bySymbol[p.symbolname].push(p);
            });
            setDhssPositions(prev => ({ ...prev, ...bySymbol }));
          })
          .catch(() => {});
      }
    }
  };

  const toggleStatus = async (strategy, e) => {
    e.stopPropagation();
    const newStatus = strategy.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    setTogglingId(strategy.id);
    try {
      await axios.patch(`/strategy/${strategy.id}/status`, { status: newStatus });
      flash(`${strategy.symbolname} set to ${newStatus}`);
      fetchStrategies();
    } catch {
      flash("Failed to update status.", false);
    } finally {
      setTogglingId(null);
    }
  };

  /* ---------------- FILTER + SORT ---------------- */

  const filteredStrategies = useMemo(() => {
    return strategies
      .filter((s) =>
        s.symbolname.toLowerCase().includes(searchSymbol.toLowerCase())
      )
      .filter((s) => (filterType ? s.strategy_type === filterType : true))
      .filter((s) => (filterStatus ? s.status === filterStatus : true))
      .sort((a, b) => a.symbolname.localeCompare(b.symbolname));
  }, [strategies, searchSymbol, filterType, filterStatus]);

  const uniqueStatuses = [...new Set(strategies.map((s) => s.status))];

  /* ---------------- RENDER ---------------- */

  return (
    <div style={{ padding: "25px", background: "#f4f6f9", minHeight: "100vh" }}>

      {msg && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          padding: "10px 20px", borderRadius: 8, color: "#fff",
          fontWeight: 600, fontSize: 14,
          background: msg.ok ? "#28a745" : "#dc3545"
        }}>
          {msg.text}
        </div>
      )}

      {/* FILTERS */}
      <div style={{
        display: "flex",
        gap: "15px",
        marginBottom: "25px",
        alignItems: "center"
      }}>
        <input
          placeholder="Search Symbol..."
          value={searchSymbol}
          onChange={(e) => setSearchSymbol(e.target.value)}
          style={inputStyle}
        />

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={inputStyle}
        >
          <option value="">All Strategies</option>
          {STRATEGY_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={inputStyle}
        >
          <option value="">All Status</option>
          {uniqueStatuses.map((status) => (
            <option key={status}>{status}</option>
          ))}
        </select>
      </div>

      {/* CARDS */}
      {filteredStrategies.map((strategy) => {
        const json = strategy.strategy_json;
        const derived = json.derived || {};
        const runtime = json.runtime || {};
        const accounting = json.accounting || {};
        const ladder = derived.ladder || [];
        const liveLtp       = strategy.live_ltp      || 0;
        const liveNetQty    = strategy.live_netqty   || 0;
        const liveAvgPrice  = strategy.live_avg_price || null;
        const snapshotAsOf  = strategy.snapshot_as_of
          ? strategy.snapshot_as_of.slice(11, 16)   // "HH:MM" from "YYYY-MM-DD HH:MM:SS"
          : null;
        // Compute DTE fresh from intent.expiry (MM/DD/YYYY) so it reflects today's date,
        // not the stale value stored in derived at last recompute time.
        const computedDTE = (() => {
          const exp = json.intent?.expiry;
          if (!exp) return null;
          const [m, d, y] = exp.split("/");
          const expiryMs = new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).getTime();
          const nowET = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
          const todayET = new Date(nowET.getFullYear(), nowET.getMonth(), nowET.getDate()).getTime();
          return Math.max(0, Math.round((expiryMs - todayET) / 86400000));
        })();

        return (
          <div key={strategy.id} style={cardStyle}>

            {/* HEADER */}
            <div
              style={headerStyle}
              onClick={() => toggleCard(strategy.id, strategy)}
            >
              <div>
                <div style={symbolStyle}>{strategy.symbolname}</div>

                <div style={{ marginTop: 4 }}>
                  <span style={typeBadge}>{strategyLabel(strategy.strategy_type)}</span>
                  <span
                    style={{
                      ...statusBadge,
                      background:
                        strategy.status === "ACTIVE"
                          ? "#28a745"
                          : "#6c757d"
                    }}
                  >
                    {strategy.status}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const existingSettings = strategy.strategy_json?.intent?.settings_snapshot;
                    if (existingSettings && strategy.symbolname) {
                      sessionStorage.setItem(
                        `rebuild_settings_${strategy.symbolname}`,
                        JSON.stringify(existingSettings)
                      );
                    }
                    window.location.href = `/strategy/build?symbol=${encodeURIComponent(strategy.symbolname)}&strategy_type=${encodeURIComponent(strategy.strategy_type)}`;
                  }}
                  style={{
                    padding: "5px 14px",
                    borderRadius: 8,
                    border: "1px solid #0066cc",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    background: "#fff",
                    color: "#0066cc",
                  }}
                >
                  Rebuild
                </button>

                <button
                  onClick={(e) => toggleStatus(strategy, e)}
                  disabled={togglingId === strategy.id}
                  style={{
                    padding: "5px 14px",
                    borderRadius: 8,
                    border: "none",
                    cursor: togglingId === strategy.id ? "not-allowed" : "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                    background: strategy.status === "ACTIVE" ? "#dc3545" : "#28a745",
                    color: "#fff",
                    opacity: togglingId === strategy.id ? 0.6 : 1,
                  }}
                >
                  {togglingId === strategy.id
                    ? "…"
                    : strategy.status === "ACTIVE" ? "Deactivate" : "Activate"}
                </button>

                {/* Kebab menu */}
                <div
                  ref={openKebabId === strategy.id ? kebabRef : null}
                  style={{ position: "relative" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenKebabId(openKebabId === strategy.id ? null : strategy.id);
                    }}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 8,
                      border: "1px solid #dee2e6",
                      cursor: "pointer",
                      fontSize: 16,
                      fontWeight: 700,
                      background: "#fff",
                      color: "#555",
                      lineHeight: 1,
                    }}
                    title="More options"
                  >
                    ⋮
                  </button>
                  {openKebabId === strategy.id && (
                    <div style={{
                      position: "absolute", right: 0, top: "calc(100% + 4px)",
                      background: "#fff", border: "1px solid #dee2e6",
                      borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                      zIndex: 100, minWidth: 130, overflow: "hidden",
                    }}>
                      <button
                        onClick={(e) => handleRemove(strategy, e)}
                        disabled={removingId === strategy.id}
                        style={{
                          display: "block", width: "100%", padding: "10px 16px",
                          background: "none", border: "none", cursor: "pointer",
                          textAlign: "left", fontSize: 13, color: "#dc3545",
                          fontWeight: 600,
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "#fff5f5"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}
                      >
                        {removingId === strategy.id ? "Removing…" : "🗑 Remove"}
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ fontSize: 18 }}>
                  {expandedId === strategy.id ? "▲" : "▼"}
                </div>
              </div>
            </div>

            {/* BODY */}
            {expandedId === strategy.id && (
              <div style={{ padding: "20px" }}>

                {/* ═══════════════════════════════════════════════
                    DELTA-HEDGED SHORT STRANGLE (v2.0 layout)
                    ═══════════════════════════════════════════════ */}
                {["DELTA_HEDGED_SHORT_STRANGLE", "WEEKLY_INDEX_SHORT_STRADDLE"].includes(strategy.strategy_type) ? (() => {
                  const intent       = json.intent || {};
                  const settingsSnap = intent.settings_snapshot || {};
                  const live         = runtime._live || {};

                  // Live delta-neutral zone data (from last cycle)
                  const dnp        = live.delta_neutral_price ?? null;
                  const upperBand  = live.upper_band ?? null;
                  const lowerBand  = live.lower_band ?? null;
                  const pctDnp     = live.percent_move_dnp ?? settingsSnap.percent_move_dnp ?? 0.5;
                  const ltp        = live.underlying_ltp ?? derived.underlying_ltp ?? null;
                  const breached   = live.price_band_breached ?? false;
                  const direction  = live.price_band_breach_direction ?? "";
                  const pctFromRef = live.pct_from_reference ?? null;

                  // Delta state — fall back to build-time derived when _live hasn't been populated yet
                  const callLots   = live.total_call_lots  ?? runtime.active_call_lots ?? derived.total_call_lots ?? 0;
                  const putLots    = live.total_put_lots   ?? runtime.active_put_lots  ?? derived.total_put_lots  ?? 0;
                  const callDelta  = live.call_delta_total != null
                    ? live.call_delta_total
                    : (derived.call_abs_delta != null && callLots > 0 ? -(derived.call_abs_delta * callLots) : null);
                  const putDelta   = live.put_delta_total != null
                    ? live.put_delta_total
                    : (derived.put_abs_delta  != null && putLots  > 0 ?  (derived.put_abs_delta  * putLots)  : null);
                  const deltaDiff  = live.delta_difference ?? derived.delta_difference ?? null;
                  const deltaThreshold = live.delta_neutral_threshold ?? derived.delta_neutral_threshold ?? null;
                  const heavier    = live.heavier_side ?? null;
                  const isNeutral  = !breached;

                  const callRange  = derived.call_delta_range || intent.call_delta_range;
                  const putRange   = derived.put_delta_range  || intent.put_delta_range;

                  const fmtRs = (v) => v != null ? formatCurrency(v, curSym) : "—";

                  return (
                    <>
                      {/* Overview */}
                      <SectionTitle title="Strategy Overview" />
                      <div style={grid4}>
                        <InfoItem label="Lot Size"          value={derived.lot_size} />
                        <InfoItem label="Initial Lots/Side" value={intent.lots_per_side ?? settingsSnap.initial_lots_per_side ?? "—"} />
                        <InfoItem label="Expiry"            value={intent.expiry} />
                        <InfoItem label="Days to Expiry"    value={computedDTE ?? derived.days_to_expiry} />
                        <InfoItem label="Underlying LTP"    value={ltp ? fmtRs(ltp) : "—"} />
                        <InfoItem label="Max Profit"        value={derived.max_profit ? fmtRs(derived.max_profit) : "—"} />
                        <InfoItem label="Upper Breakeven"   value={derived.call_breakeven ? fmtRs(derived.call_breakeven) : "—"} />
                        <InfoItem label="Lower Breakeven"   value={derived.put_breakeven  ? fmtRs(derived.put_breakeven)  : "—"} />
                      </div>

                      {/* Delta Neutral Zone — live computed each cycle */}
                      <SectionTitle title="Delta Neutral Zone" />
                      {dnp ? (
                        <>
                          {/* Band breach status banner */}
                          <div style={{
                            display: "flex", alignItems: "center", gap: 10,
                            background: breached ? "#fde2e2" : "#d4edda",
                            borderRadius: 8, padding: "10px 14px", marginBottom: 14,
                          }}>
                            <span style={{ fontSize: 20 }}>{breached ? "⚠" : "✓"}</span>
                            <div>
                              <div style={{ fontWeight: 700, color: breached ? "#dc3545" : "#155724", fontSize: 14 }}>
                                {breached
                                  ? `Price breached ${direction === "UP" ? "upper" : "lower"} threshold — rebalancing needed`
                                  : "Position is delta neutral — within the band"
                                }
                              </div>
                              {pctFromRef != null && (
                                <div style={{ fontSize: 12, color: "#555" }}>
                                  {pctFromRef >= 0 ? "+" : ""}{pctFromRef}% from delta-neutral point
                                </div>
                              )}
                            </div>
                          </div>

                          <div style={grid4}>
                            <InfoItem label="Delta-Neutral Price (DNP)" value={<span style={{ fontWeight: 700, color: "#2c3e50" }}>{fmtRs(dnp)}</span>} />
                            <InfoItem label="Band Width (% Move DNP)"   value={`±${pctDnp}%`} />
                            <InfoItem label="Upper Threshold"           value={<span style={{ fontWeight: 700, color: "#28a745" }}>{fmtRs(upperBand)}</span>} />
                            <InfoItem label="Lower Threshold"           value={<span style={{ fontWeight: 700, color: "#dc3545" }}>{fmtRs(lowerBand)}</span>} />
                            <InfoItem label="Delta Imbalance Threshold" value={deltaThreshold != null ? deltaThreshold.toFixed(4) : "—"} />
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
                          Delta-neutral zone will be computed on the first cycle run.
                        </div>
                      )}

                      {/* Active Legs — driven by live optionpositions, one row per position */}
                      <SectionTitle title="Active Legs" />
                      <div style={{ overflowX: "auto" }}>
                        {(() => {
                          const livePosForSym = (dhssPositions[strategy.symbolname] || [])
                            .filter(p => p.optiontype === "CE" || p.optiontype === "PE")
                            .filter(p => p.netqty !== 0)
                            .sort((a, b) => a.optiontype.localeCompare(b.optiontype) || a.strikeprice - b.strikeprice);
                          // Fall back to the old summary rows when live data not yet loaded
                          if (livePosForSym.length === 0) {
                            return (
                              <table style={tableStyle}>
                                <thead>
                                  <tr style={{ background: "#f8f9fb" }}>
                                    {["Leg","Symbol","Strike","Lots","Net Delta","Delta/Lot","Premium (LTP)"].map(c => (
                                      <th key={c} style={thStyle}>{c}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr style={{ background: "#fff8f0" }}>
                                    <td style={tdStyle}><strong style={{ color: "#28a745" }}>CE Short</strong></td>
                                    <td style={tdStyle}>{runtime.active_call_symbol ?? derived.call_symbol ?? "—"}</td>
                                    <td style={tdStyle}>{derived.call_strike ? fmtRs(derived.call_strike) : "—"}</td>
                                    <td style={{ ...tdStyle, fontWeight: 700, color: heavier === "CALL" ? "#dc3545" : "#2c3e50" }}>
                                      {callLots || "—"}{heavier === "CALL" ? " ▲" : ""}
                                    </td>
                                    <td style={tdStyle}>{callDelta != null ? callDelta.toFixed(4) : "—"}</td>
                                    <td style={tdStyle}>{callDelta != null && callLots > 0 ? (callDelta / callLots).toFixed(4) : "—"}</td>
                                    <td style={tdStyle}><strong>{derived.call_ltp ? fmtRs(derived.call_ltp) : "—"}</strong></td>
                                  </tr>
                                  <tr style={{ background: "#f0f8ff" }}>
                                    <td style={tdStyle}><strong style={{ color: "#dc3545" }}>PE Short</strong></td>
                                    <td style={tdStyle}>{runtime.active_put_symbol ?? derived.put_symbol ?? "—"}</td>
                                    <td style={tdStyle}>{derived.put_strike ? fmtRs(derived.put_strike) : "—"}</td>
                                    <td style={{ ...tdStyle, fontWeight: 700, color: heavier === "PUT" ? "#dc3545" : "#2c3e50" }}>
                                      {putLots || "—"}{heavier === "PUT" ? " ▲" : ""}
                                    </td>
                                    <td style={tdStyle}>{putDelta != null ? putDelta.toFixed(4) : "—"}</td>
                                    <td style={tdStyle}>{putDelta != null && putLots > 0 ? (putDelta / putLots).toFixed(4) : "—"}</td>
                                    <td style={tdStyle}><strong>{derived.put_ltp ? fmtRs(derived.put_ltp) : "—"}</strong></td>
                                  </tr>
                                </tbody>
                              </table>
                            );
                          }
                          return (
                            <table style={tableStyle}>
                              <thead>
                                <tr style={{ background: "#f8f9fb" }}>
                                  {["Leg","Symbol","Strike","Lots","Net Delta","Delta/Lot","LTP","Unrealised"].map(c => (
                                    <th key={c} style={thStyle}>{c}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {livePosForSym.map((p, i) => {
                                  const lots  = Math.abs(p.totallots || 0);
                                  const delta = p.delta != null ? Number(p.delta) : null;
                                  const isHeavierType = (p.optiontype === "CE" && heavier === "CALL") || (p.optiontype === "PE" && heavier === "PUT");
                                  return (
                                    <tr key={i} style={{ background: p.optiontype === "CE" ? "#fff8f0" : "#f0f8ff" }}>
                                      <td style={tdStyle}>
                                        <strong style={{ color: p.optiontype === "CE" ? "#28a745" : "#dc3545" }}>
                                          {p.optiontype} Short
                                        </strong>
                                      </td>
                                      <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: 11 }}>
                                        {p.symbolname}{p.expirydate ? p.expirydate.replace(/-/g,"").slice(2) : ""}{p.strikeprice}{p.optiontype}
                                      </td>
                                      <td style={tdStyle}>{fmtRs(p.strikeprice)}</td>
                                      <td style={{ ...tdStyle, fontWeight: 700, color: isHeavierType ? "#dc3545" : "#2c3e50" }}>
                                        {lots}{isHeavierType ? " ▲" : ""}
                                      </td>
                                      <td style={tdStyle}>{delta != null ? delta.toFixed(4) : "—"}</td>
                                      <td style={tdStyle}>{delta != null && lots > 0 ? (delta / lots).toFixed(4) : "—"}</td>
                                      <td style={tdStyle}><strong>{p.ltp != null ? fmtRs(p.ltp) : "—"}</strong></td>
                                      <td style={{ ...tdStyle, color: (p.unrealised || 0) >= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>
                                        {p.unrealised != null ? formatCurrencySigned(p.unrealised, curSym, 0) : "—"}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr style={{ background: "#f8f9fb", borderTop: "2px solid #dee2e6" }}>
                                  <td colSpan={5} style={{ ...tdStyle, fontWeight: 700, color: "#555" }}>Net delta imbalance</td>
                                  <td colSpan={3} style={{ ...tdStyle, fontWeight: 700, color: deltaThreshold != null && deltaDiff != null && deltaDiff > deltaThreshold ? "#dc3545" : "#28a745" }}>
                                    {deltaDiff != null ? deltaDiff.toFixed(4) : "—"}
                                    {heavier && deltaDiff > 0.001 ? ` (${heavier} heavier)` : ""}
                                    {deltaThreshold != null ? ` — Threshold: ${deltaThreshold.toFixed(4)}` : ""}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          );
                        })()}
                      </div>

                      {/* Delta Ranges */}
                      <SectionTitle title="Delta Settings" />
                      <div style={grid4}>
                        <InfoItem label="Call Delta Range" value={callRange ? `${callRange[0]} – ${callRange[1]}` : "—"} />
                        <InfoItem label="Put Delta Range"  value={putRange  ? `${putRange[0]}  – ${putRange[1]}`  : "—"} />
                        <InfoItem label="Min Sell Delta"   value={settingsSnap.min_sell_delta ?? "—"} />
                        <InfoItem label="Max Sell Delta"   value={settingsSnap.max_sell_delta ?? "—"} />
                      </div>

                      {/* Payoff Diagram */}
                      <SectionTitle title="Payoff Diagram at Expiry" />
                      <div style={{ background: "#fafbfc", borderRadius: 8, padding: "16px 12px", border: "1px solid #eee" }}>
                        <PayoffChart
                          strategyType={strategy.strategy_type}
                          derived={derived}
                          reference={json.reference}
                          intent={json.intent}
                        />
                      </div>
                    </>
                  );
                })() : strategy.strategy_type === "WEEKLY_INDEX_SHORT_STRADDLE" ? (() => {
                  /* ═══════════════════════════════════════════════
                     WEEKLY INDEX SHORT STRADDLE layout
                     ═══════════════════════════════════════════════ */
                  const intent = json.intent || {};
                  const isOpenToday = !!runtime.is_open;
                  const fmtRs = (v) => v != null ? formatCurrency(v, curSym) : "—";

                  return (
                    <>
                      <SectionTitle title="Strategy Overview" />
                      <div style={grid4}>
                        <InfoItem label="Call/Put Pairs" value={intent.pairs ?? "—"} />
                        <InfoItem label="Session Date"   value={runtime.session_date ?? "—"} />
                        <InfoItem
                          label="Today's Straddle"
                          value={
                            isOpenToday
                              ? <span style={{ color: "#28a745", fontWeight: 700 }}>Open</span>
                              : <span style={{ color: "#6c757d", fontWeight: 700 }}>Closed</span>
                          }
                        />
                        <InfoItem label="Active Expiry" value={runtime.active_expiry ?? "—"} />
                      </div>

                      <SectionTitle title="Active Legs" />
                      <div style={{ overflowX: "auto" }}>
                        <table style={tableStyle}>
                          <thead>
                            <tr style={{ background: "#f8f9fb" }}>
                              {["Leg", "Symbol", "Strike"].map(c => (
                                <th key={c} style={thStyle}>{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={{ background: "#fff8f0" }}>
                              <td style={tdStyle}><strong style={{ color: "#28a745" }}>CE Short</strong></td>
                              <td style={tdStyle}>{runtime.active_call_symbol ?? "—"}</td>
                              <td style={tdStyle}>{runtime.active_call_strike ? fmtRs(runtime.active_call_strike) : "—"}</td>
                            </tr>
                            <tr style={{ background: "#f0f8ff" }}>
                              <td style={tdStyle}><strong style={{ color: "#dc3545" }}>PE Short</strong></td>
                              <td style={tdStyle}>{runtime.active_put_symbol ?? "—"}</td>
                              <td style={tdStyle}>{runtime.active_put_strike ? fmtRs(runtime.active_put_strike) : "—"}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <SectionTitle title="Daily Schedule" />
                      <div style={grid4}>
                        <InfoItem label="Open Time"              value={intent.settings_snapshot?.open_time ?? "—"} />
                        <InfoItem label="Adjustment Cutoff Time" value={intent.settings_snapshot?.adjustment_cutoff_time ?? "—"} />
                        <InfoItem label="EOD Close Time"         value={intent.settings_snapshot?.eod_close_time ?? "—"} />
                      </div>
                    </>
                  );
                })() : (
                  /* ═══════════════════════════════════════════════
                     PYRAMID HEDGING layout
                     ═══════════════════════════════════════════════ */
                  <>
                    <SectionTitle title="Strategy Overview" />
                    <div style={grid4}>
                      <InfoItem label="Lot Size" value={derived.lot_size} />
                      <InfoItem label="Call Symbol" value={derived.call_symbol} />
                      <InfoItem label="Call Strike" value={derived.call_strike} />
                      <InfoItem label="Call LTP (Premium)" value={derived.call_ltp ? formatCurrency(derived.call_ltp, curSym) : "—"} />
                      <InfoItem label="Call Delta" value={derived.call_delta} />
                      <InfoItem label="Target Lots" value={derived.target_lots} />
                      <InfoItem label="Hedge %" value={derived.hedge_percent} />
                      {strategy.strategy_type === "FUTURES_PYRAMID_HEDGING" && (
                        <>
                          <InfoItem label="Hedge Exit Floor %" value={json.intent?.settings_snapshot?.hedge_exit_floor_pct ?? "—"} />
                          <InfoItem label="Hedge Instrument" value={json.intent?.settings_snapshot?.hedge_instrument ?? "—"} />
                        </>
                      )}
                      <InfoItem label="Reference Avg" value={derived.reference_avg_price} />
                      <InfoItem label="Days to Expiry" value={computedDTE ?? derived.days_to_expiry} />
                      <InfoItem
                        label="Tail Risk P&L"
                        value={(() => {
                          const tr = accounting.tail_risk_pnl ?? derived.tail_risk_pnl;
                          return tr != null
                            ? <span style={{ color: tr >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>
                                {formatCurrencySigned(tr, curSym, 0)}
                              </span>
                            : "—";
                        })()}
                      />
                    </div>

                    {derived.csp_enabled && (
                      <>
                        <SectionTitle title="CSP (Cash Secured Put)" />
                        <div style={grid4}>
                          <InfoItem label="Put Symbol"      value={runtime.active_csp ?? derived.csp_symbol ?? "—"} />
                          <InfoItem label="Put Strike"      value={derived.put_strike  ? formatCurrency(derived.put_strike, curSym)  : "—"} />
                          <InfoItem label="Sold At"         value={derived.put_sold_ltp != null ? formatCurrency(derived.put_sold_ltp, curSym) : "—"} />
                          <InfoItem label="Sold Amount"     value={
                            derived.put_sold_ltp && derived.lot_size
                              ? formatCurrency(derived.put_sold_ltp * derived.lot_size, curSym)
                              : "—"
                          } />
                          <InfoItem label="Put LTP (Live)"  value={derived.put_ltp     ? formatCurrency(derived.put_ltp, curSym)     : "—"} />
                          <InfoItem label="Put Delta"       value={derived.put_delta   ?? "—"} />
                          <InfoItem label="Put Distance %"  value={derived.put_distance_pct != null ? `${derived.put_distance_pct}%` : "—"} />
                          <InfoItem label="CSP Placed"      value={
                            runtime.csp_placed
                              ? <span style={{ color: "#28a745", fontWeight: 700 }}>Yes</span>
                              : <span style={{ color: "#fd7e14", fontWeight: 700 }}>Pending</span>
                          } />
                        </div>
                      </>
                    )}

                    {json.intent?.settings_snapshot?.hedge_instrument === "SYNTHETIC_STOCK" && (
                      <>
                        <SectionTitle title="Synthetic Stock (Long Call + Short Put)" />
                        <div style={grid4}>
                          <InfoItem label="Call Symbol" value={runtime.synthetic_call_symbol ?? "—"} />
                          <InfoItem label="Put Symbol"  value={runtime.synthetic_put_symbol ?? "—"} />
                          <InfoItem label="Strike"      value={runtime.synthetic_strike ? formatCurrency(runtime.synthetic_strike, curSym) : "—"} />
                          <InfoItem label="Expiry"      value={runtime.synthetic_expiry ?? "—"} />
                          <InfoItem label="Lots"        value={runtime.synthetic_lots ?? "—"} />
                        </div>
                      </>
                    )}

                    <SectionTitle title="Execution Snapshot" />
                    <div style={grid3}>
                      <InfoItem
                        label={snapshotAsOf ? `Stock LTP (as of ${snapshotAsOf})` : "Stock LTP"}
                        value={liveLtp > 0 ? `$${liveLtp}` : (derived.underlying_ltp ? `$${derived.underlying_ltp}` : "—")}
                      />
                      <InfoItem
                        label={strategy.strategy_type === "FUTURES_PYRAMID_HEDGING" ? "Current Futures Qty" : "Current Net Qty"}
                        value={liveNetQty > 0 ? liveNetQty        : (runtime.current_netqty ?? runtime.netqty ?? "—")}
                      />
                      <InfoItem
                        label={strategy.strategy_type === "FUTURES_PYRAMID_HEDGING" ? "Futures Avg Price" : "Stock Avg Price"}
                        value={liveAvgPrice != null ? formatCurrency(liveAvgPrice, curSym) : "—"}
                      />
                      <InfoItem label="Gap %"           value={derived.gap_pct} />
                      <InfoItem
                        label={strategy.strategy_type === "FUTURES_PYRAMID_HEDGING" ? "Next Futures Buy Price" : "Next Buy Price"}
                        value={derived.next_buy_price}
                      />
                      <InfoItem
                        label={strategy.strategy_type === "FUTURES_PYRAMID_HEDGING" ? "Next Futures Buy Qty" : "Next Buy Qty"}
                        value={derived.next_buy_quantity}
                      />
                      <InfoItem label="Achievable Lots" value={derived.achievable_lots} />
                    </div>

                    <SectionTitle title="Ladder Structure" />
                    <div style={{ overflowX: "auto" }}>
                      <table style={tableStyle}>
                        <thead>
                          <tr style={{ background: "#f8f9fb" }}>
                            {["Step","Qty","Avg Price","Buy Price","Distance %","Total Qty","Investment","Lots","Est. Hedge %","Stock Profit","Options Profit","Net Profit"]
                              .map((col) => (
                              <th key={col} style={col === "Est. Hedge %" ? { ...thStyle, color: "#545cd8", whiteSpace: "nowrap" } : thStyle}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {ladder.map((row, idx) => {
                            const callQty    = (derived.target_lots || 1) * (derived.lot_size || 1);
                            const callPrem   = derived.call_ltp || 0;
                            const callStrike = derived.call_strike || 0;
                            const putPrem    = derived.csp_enabled ? (derived.put_ltp || 0) : 0;
                            const putQty     = derived.csp_enabled ? (derived.lot_size || 1) : 0;
                            const fullPremium = Math.round(
                              accounting.current_cycle_premium > 0
                                ? accounting.current_cycle_premium
                                : (callPrem * callQty + putPrem * putQty)
                            );
                            // P&L if stock reaches this step's buy price.
                            // Stock  = unrealized gain on all accumulated shares at buy_price.
                            // Options = premium received minus call intrinsic loss (call is ITM
                            //   when stock > call_strike, reducing the option profit).
                            // Net Realized = all booked option losses/gains + stock sales.
                            // Note: at the last step when qty = callQty and buy_price >> call_strike,
                            // (stock gain − call intrinsic) collapses to (callStrike − avg) × qty,
                            // so the last step automatically equals the expiry Tail Risk P&L.
                            const stockProfit        = Math.round((row.buy_price - row.avg_price) * row.total_qty);
                            const callIntrinsic      = callStrike > 0 ? Math.max(0, row.buy_price - callStrike) * callQty : 0;
                            const optionsProfit      = Math.round(fullPremium - callIntrinsic);
                            const netProfit          = stockProfit + optionsProfit;
                            const profitColor = (v) => v == null ? "#888" : v >= 0 ? "#28a745" : "#dc3545";
                            const fmt = (v) => v == null ? "—" : `$${v.toLocaleString("en-US")}`;
                            const hedgePct = row.projected_hedge_pct != null ? row.projected_hedge_pct : null;
                            const stepLtp = liveLtp > 0 ? liveLtp : (derived.underlying_ltp || 0);
                            const distPct = stepLtp > 0 ? ((row.buy_price - stepLtp) / stepLtp * 100) : null;
                            return (
                              <tr key={idx} style={{ background: idx % 2 === 0 ? "#ffffff" : "#fafbfc" }}>
                                <td style={tdStyle}>{row.step}</td>
                                <td style={tdStyle}>{row.qty}</td>
                                <td style={tdStyle}>{row.avg_price}</td>
                                <td style={tdStyle}>{row.buy_price}</td>
                                <td style={{ ...tdStyle, color: distPct == null ? "#888" : distPct <= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>
                                  {distPct != null ? `${distPct >= 0 ? "+" : ""}${distPct.toFixed(2)}%` : "—"}
                                </td>
                                <td style={tdStyle}>{row.total_qty}</td>
                                <td style={tdStyle}>{row.investment}</td>
                                <td style={tdStyle}>{row.stock_lots}</td>
                                <td style={{ ...tdStyle, color: "#545cd8", fontWeight: 600 }}>
                                  {hedgePct != null ? `~${hedgePct}%` : "—"}
                                </td>
                                <td style={{ ...tdStyle, color: profitColor(stockProfit),   fontWeight: 600 }}>{fmt(stockProfit)}</td>
                                <td style={{ ...tdStyle, color: profitColor(optionsProfit), fontWeight: 600 }}>{fmt(optionsProfit)}</td>
                                <td style={{ ...tdStyle, color: profitColor(netProfit),     fontWeight: 600 }}>{fmt(netProfit)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ fontSize: 11, color: "#888", marginTop: 6, fontStyle: "italic" }}>
                        † Est. Hedge % is a forward projection of delta-weighted hedge at each price level. Actual hedge varies with time to expiry and implied volatility. Live hedge_pct is tracked every cycle.<br/>
                        ‡ Each row shows P&amp;L if the stock reaches that step's buy price. Stock = unrealized gain at that price. Options = premium received minus call intrinsic (when stock &gt; call strike, the call is ITM and reduces profit). Net = Stock + Options at that price level. At the last step this equals the Tail Risk P&amp;L.
                      </div>
                    </div>

                    <SectionTitle title="Payoff Diagram at Expiry" />
                    <div style={{ background: "#fafbfc", borderRadius: 8, padding: "16px 12px", border: "1px solid #eee" }}>
                      <PayoffChart
                        strategyType={strategy.strategy_type}
                        derived={{
                          ...derived,
                          options_income: accounting.current_cycle_premium > 0
                            ? accounting.current_cycle_premium
                            : undefined,
                        }}
                        reference={json.reference}
                        intent={json.intent}
                      />
                    </div>
                  </>
                )}

                {/* ACCOUNTING */}
                {Object.keys(accounting).length > 0 && (
                  <>
                    <SectionTitle title="Accounting" />
                    {["DELTA_HEDGED_SHORT_STRANGLE", "WEEKLY_INDEX_SHORT_STRADDLE"].includes(strategy.strategy_type) ? (
                      <div style={grid3}>
                        <InfoItem label="Premium Collected (Open)" value={accounting.premium_collected_total != null ? `$${accounting.premium_collected_total.toLocaleString()}` : "—"} />
                        <InfoItem label="Booked Profit (Closed)"   value={accounting.booked_profit           != null ? `$${accounting.booked_profit.toLocaleString()}`           : "—"} />
                        <InfoItem label="Net Potential Profit"      value={accounting.net_potential_profit    != null ? `$${accounting.net_potential_profit.toLocaleString()}`    : "—"} />
                      </div>
                    ) : strategy.strategy_type === "WEEKLY_INDEX_SHORT_STRADDLE" ? (
                      <div style={grid3}>
                        <InfoItem label="Premium Collected Today" value={accounting.premium_collected_today != null ? formatCurrency(accounting.premium_collected_today, curSym) : "—"} />
                        <InfoItem label="Booked Profit Today"      value={accounting.booked_profit_today      != null ? formatCurrency(accounting.booked_profit_today, curSym)      : "—"} />
                      </div>
                    ) : (
                      <div style={grid4}>
                        <InfoItem label="Current Expiry Potential Profit" value={accounting.current_expiry_potential_profit != null ? <span style={{ color: accounting.current_expiry_potential_profit >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>{formatCurrencySigned(accounting.current_expiry_potential_profit, curSym, 0)}</span> : "—"} />
                        <InfoItem label="Expiry Open Price"            value={json.reference?.expiry_open_price              != null ? formatCurrency(json.reference.expiry_open_price, curSym)           : "—"} />
                        <InfoItem label="Expiry Effective Price"       value={accounting.expiry_effective_price              != null ? <span style={{ color: "#0055aa", fontWeight: 700 }}>{formatCurrency(accounting.expiry_effective_price, curSym)}</span> : "—"} />
                        <InfoItem label="Current Cycle Premium"        value={accounting.current_cycle_premium   != null ? formatCurrency(accounting.current_cycle_premium, curSym)   : "—"} />
                        {strategy.strategy_type === "FUTURES_PYRAMID_HEDGING"
                          ? (accounting.realized_pnl_by_expiry && Object.keys(accounting.realized_pnl_by_expiry).length > 0
                              ? Object.entries(accounting.realized_pnl_by_expiry).map(([expiry, pnl]) => {
                                  const d = new Date(expiry + "T00:00:00");
                                  const label = `Booked (${d.toLocaleString("en-US", { month: "short", year: "numeric" })})`;
                                  return <InfoItem key={expiry} label={label} value={<span style={{ color: pnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>{formatCurrencySigned(pnl, curSym, 0)}</span>} />;
                                })
                              : null)
                          : <InfoItem label="Net Realized Profit" value={accounting.net_realized_profit != null ? <span style={{ color: accounting.net_realized_profit >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>{formatCurrencySigned(accounting.net_realized_profit, curSym, 0)}</span> : "—"} />
                        }
                        <InfoItem label="Premium per Share"            value={accounting.premium_per_share       != null ? formatCurrency(accounting.premium_per_share, curSym)                         : "—"} />
                        <InfoItem label="Effective Avg Price"          value={accounting.effective_avg_price     != null ? formatCurrency(accounting.effective_avg_price, curSym)                       : "—"} />
                        <InfoItem label="EBP (Cumulative Floor)"       value={accounting.ebp                     != null ? formatCurrency(accounting.ebp, curSym)                                       : "—"} />
                        {strategy.strategy_type === "FUTURES_PYRAMID_HEDGING" && (
                          <>
                            <InfoItem label="Net Profit" value={accounting.current_net_profit != null ? <span style={{ color: accounting.current_net_profit >= 0 ? "#28a745" : "#dc3545", fontWeight: 700 }}>{formatCurrencySigned(accounting.current_net_profit, curSym, 0)}</span> : "—"} />
                            <InfoItem label="Downside Min Profit (CSM) Price" value={accounting.downside_min_csm_price != null ? formatCurrency(accounting.downside_min_csm_price, curSym) : "—"} />
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* METADATA */}
                <SectionTitle title="Metadata" />
                <div style={metaStyle}>
                  Created: {strategy.created_at} <br />
                  Updated: {strategy.updated_at}
                </div>

              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ---------------- UI COMPONENTS ---------------- */

const SectionTitle = ({ title }) => (
  <h4 style={{ marginTop: 25, marginBottom: 15, fontSize: 14, fontWeight: 600 }}>
    {title}
  </h4>
);

const InfoItem = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, color: "#777" }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{value ?? "-"}</div>
  </div>
);

/* ---------------- STYLES ---------------- */

const cardStyle = {
  background: "#fff",
  borderRadius: 12,
  marginBottom: 20,
  boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
};

const thStyle = {
  padding: "10px",
  textAlign: "left",
  borderBottom: "1px solid #e5e7eb",
  borderRight: "1px solid #f0f0f0",
  fontWeight: 600,
  fontSize: 12
};

const tdStyle = {
  padding: "8px 10px",
  borderBottom: "1px solid #f0f0f0",
  borderRight: "1px solid #f7f7f7"
};

const headerStyle = {
  padding: 16,
  cursor: "pointer",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: "1px solid #eee"
};

const symbolStyle = {
  fontSize: 16,   // smaller & professional
  fontWeight: 600,
  letterSpacing: 0.5
};

const typeBadge = {
  fontSize: 11,
  background: "#007bff",
  color: "#fff",
  padding: "3px 8px",
  borderRadius: 12,
  marginRight: 8
};

const statusBadge = {
  fontSize: 11,
  color: "#fff",
  padding: "3px 8px",
  borderRadius: 12
};

const inputStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #ccc",
  fontSize: 13
};

const grid4 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 20
};

const grid3 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  //background: "#f8f9fb",
  gap: 20
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13
};

const metaStyle = {
  fontSize: 12,
  color: "#666",
  marginTop: 10
};

export default StrategyManage;